import React, { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Users, Plus, Copy, Mail, UserPlus, Settings, Trash2, 
  ArrowLeft, Crown, User, Baby, ChevronRight, Home,
  Wallet, Target, BarChart3, PiggyBank, TrendingUp, TrendingDown,
  Check, X, Share2, LogOut, Banknote, ArrowUpRight, Pencil, RefreshCw, ArrowRightLeft, Zap,
  Gift, Clock, CheckCircle, XCircle, Send, ChevronDown, ChevronUp
} from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import BottomNav from '../components/BottomNav';
import SpendingByMember from '../components/SpendingByMember';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Progress } from '../components/ui/progress';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { 
  getFamily, createFamily, deleteFamily, updateFamilySettings,
  addFamilyMember, removeFamilyMember, updateFamilyMember,
  inviteToFamily, joinFamily, leaveFamily, getFamilyStats,
  getPotCommunStatus, contributeToPotCommun, updateProfile,
  cancelFamilyInvite, resendFamilyInvite, transferFamilyOwnership,
  getPocketMoneyRequests, respondToPocketMoneyRequest, transferPocketMoney,
  checkUsername, validatePassword, createChildAccount,
  getSubscriptionStats, getPocketMoneyStats
} from '../lib/api';
import { formatCurrency, getCategoryColor } from '../lib/utils';
import { toast } from 'sonner';
import { useTutorial } from '../components/OnboardingTutorial';

const OnboardingTutorial = lazy(() => import('../components/OnboardingTutorial'));

export default function FamilyPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, checkAuth } = useAuth();
  const { t, language } = useLanguage();
  const { shouldShow: showTutorial } = useTutorial();
  
  // Check if user is single - redirect to settings with a message
  const isSingle = user?.situation === 'single';
  
  const [family, setFamily] = useState(null);
  const [stats, setStats] = useState(null);
  const [potCommun, setPotCommun] = useState(null);
  const [subscriptionStats, setSubscriptionStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [initialInviteCode, setInitialInviteCode] = useState('');
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showContributeModal, setShowContributeModal] = useState(false);
  const [showEditSalaryModal, setShowEditSalaryModal] = useState(false);
  const [editingSalary, setEditingSalary] = useState('');
  const [editingIncomeType, setEditingIncomeType] = useState('employed');
  const [savingSalary, setSavingSalary] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState(null);
  const [deletingMember, setDeletingMember] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferringOwnership, setTransferringOwnership] = useState(false);
  const [codeCopied, setCodeCopied] = useState(false);
  // Pocket money states
  const [pocketMoneyRequests, setPocketMoneyRequests] = useState([]);
  const [showGivePocketMoneyModal, setShowGivePocketMoneyModal] = useState(false);
  const [selectedChildForPocketMoney, setSelectedChildForPocketMoney] = useState(null);
  const [pocketMoneyAmount, setPocketMoneyAmount] = useState('');
  const [pocketMoneySource, setPocketMoneySource] = useState('personal'); // 'personal' or 'common'
  const [respondingToRequest, setRespondingToRequest] = useState(null);
  
  // Edit child pocket money states
  const [showEditChildModal, setShowEditChildModal] = useState(false);
  const [editingChild, setEditingChild] = useState(null);
  const [editChildPocketMoneyEnabled, setEditChildPocketMoneyEnabled] = useState(false);
  const [editChildAllowance, setEditChildAllowance] = useState('');
  const [savingChildSettings, setSavingChildSettings] = useState(false);
  
  // Pocket money stats dashboard
  const [pocketMoneyStats, setPocketMoneyStats] = useState(null);
  const [expandedChildStats, setExpandedChildStats] = useState(null);
  const [tutorialActive, setTutorialActive] = useState(false);

  // Redirect single users to settings
  useEffect(() => {
    if (isSingle) {
      toast.info(
        language === 'fr' 
          ? 'Changez votre situation dans les paramètres pour accéder aux fonctionnalités famille'
          : language === 'pt'
          ? 'Altere sua situação nas configurações para acessar os recursos da família'
          : 'Change your situation in settings to access family features'
      );
      navigate('/settings');
    }
  }, [isSingle, navigate, language]);

  useEffect(() => {
    if (!isSingle) {
      loadData();
    }
  }, [isSingle]);

  // Show tutorial after component is mounted
  useEffect(() => {
    if (showTutorial) {
      const timer = setTimeout(() => setTutorialActive(true), 500);
      return () => clearTimeout(timer);
    }
  }, [showTutorial]);

  // Check for invite code from navigation state or sessionStorage
  useEffect(() => {
    const stateCode = location.state?.inviteCode;
    const storedCode = sessionStorage.getItem('spenspot_invite_code');
    const code = stateCode || storedCode;
    
    if (code && !user?.family_id) {
      setInitialInviteCode(code.toUpperCase());
      setShowJoinModal(true);
      // Clear from sessionStorage after use
      sessionStorage.removeItem('spenspot_invite_code');
    }
  }, [location.state, user?.family_id]);

  const loadData = async () => {
    try {
      setLoading(true);
      const familyRes = await getFamily();
      
      if (familyRes.data.family) {
        // Add pending invites to family object
        const familyData = familyRes.data.family;
        familyData.pending_invites = familyRes.data.pending_invites || [];
        setFamily(familyData);
        
        const statsRes = await getFamilyStats();
        setStats(statsRes.data);
        
        // Load pot commun status only in hybrid mode (in shared mode, everything is already pooled)
        if (familyData.budget_mode === 'hybrid') {
          try {
            const potRes = await getPotCommunStatus();
            setPotCommun(potRes.data);
          } catch (e) {
            console.log('Pot commun not available');
          }
        }
        
        // Load pocket money requests (for parents)
        try {
          const requestsRes = await getPocketMoneyRequests('pending');
          setPocketMoneyRequests(requestsRes.data.requests || []);
        } catch (e) {
          console.log('Pocket money requests not available');
        }
        
        // Load pocket money stats (for parents dashboard)
        if (user?.family_role !== 'child') {
          try {
            const pmStatsRes = await getPocketMoneyStats();
            setPocketMoneyStats(pmStatsRes.data);
          } catch (e) {
            console.log('Pocket money stats not available');
          }
        }
        
        // Load subscription stats
        try {
          const subStatsRes = await getSubscriptionStats();
          setSubscriptionStats(subStatsRes.data);
        } catch (e) {
          console.log('Subscription stats not available');
        }
      } else {
        setFamily(null);
      }
    } catch (error) {
      console.error('Failed to load family data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyCode = async () => {
    if (family?.invite_code) {
      await navigator.clipboard.writeText(family.invite_code);
      setCodeCopied(true);
      setTimeout(() => setCodeCopied(false), 2000);
      toast.success(t('copied'));
    }
  };

  const handleShare = async () => {
    if (family?.invite_code && navigator.share) {
      try {
        await navigator.share({
          title: t('join_my_household'),
          text: t('join_my_household_text').replace('{name}', family.name).replace('{code}', family.invite_code),
          url: window.location.origin
        });
      } catch (err) {
        handleCopyCode();
      }
    } else {
      handleCopyCode();
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'owner': return Crown;
      case 'adult': return User;
      case 'child': return Baby;
      default: return User;
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'owner': return 'text-amber-500';
      case 'adult': return 'text-blue-500';
      case 'child': return 'text-pink-500';
      default: return 'text-muted-foreground';
    }
  };

  // Handle salary editing
  const handleOpenEditSalary = () => {
    setEditingSalary(user?.salary?.toString() || '');
    setEditingIncomeType(user?.income_type || 'employed');
    setShowEditSalaryModal(true);
  };

  const handleSaveSalary = async () => {
    const isEmployed = editingIncomeType === 'employed';
    const newSalary = isEmployed ? parseFloat(editingSalary) : 0;
    if (isEmployed && (isNaN(newSalary) || newSalary < 0)) {
      toast.error(t('enter_valid_amount'));
      return;
    }

    setSavingSalary(true);
    try {
      await updateProfile({
        salary: newSalary,
        income_type: editingIncomeType,
      });
      await checkAuth(); // Refresh user data
      await loadData(); // Refresh family data to see the synced salary
      setShowEditSalaryModal(false);
      toast.success(t('salary_updated'));
    } catch (error) {
      console.error('Failed to update salary:', error);
      toast.error(t('failed_to_update_salary'));
    } finally {
      setSavingSalary(false);
    }
  };

  // Handle member deletion (owner only)
  const handleDeleteMember = async () => {
    if (!memberToDelete) return;
    
    setDeletingMember(true);
    try {
      await removeFamilyMember(memberToDelete.member_id);
      toast.success(
        t('member_removed').replace('{name}', memberToDelete.name)
      );
      setMemberToDelete(null);
      await loadData(); // Refresh to update total income
    } catch (error) {
      console.error('Failed to remove member:', error);
      toast.error(error.response?.data?.detail || t('failed_to_remove_member'));
    } finally {
      setDeletingMember(false);
    }
  };

  // Check if current user is the family owner
  const isOwner = family?.members?.some(m => m.user_id === user?.user_id && m.role === 'owner');

  // Get eligible members for ownership transfer (adults with linked accounts, not current owner)
  const eligibleForTransfer = family?.members?.filter(
    m => m.role === 'adult' && m.user_id && m.user_id !== user?.user_id
  ) || [];

  // Handle ownership transfer
  const handleTransferOwnership = async (newOwnerMemberId) => {
    setTransferringOwnership(true);
    try {
      await transferFamilyOwnership(newOwnerMemberId);
      toast.success(t('ownership_transferred'));
      setShowTransferModal(false);
      await loadData();
      await checkAuth(); // Refresh user state
    } catch (error) {
      console.error('Failed to transfer ownership:', error);
      toast.error(error.response?.data?.detail || t('failed_to_transfer'));
    } finally {
      setTransferringOwnership(false);
    }
  };

  // Open edit child modal
  const openEditChildModal = (child) => {
    setEditingChild(child);
    setEditChildPocketMoneyEnabled(child.has_pocket_money || false);
    setEditChildAllowance(child.pocket_money_allowance?.toString() || '');
    setShowEditChildModal(true);
  };

  // Save child pocket money settings
  const handleSaveChildSettings = async () => {
    if (!editingChild) return;
    
    setSavingChildSettings(true);
    try {
      await updateFamilyMember(editingChild.member_id, {
        has_pocket_money: editChildPocketMoneyEnabled,
        pocket_money_allowance: editChildPocketMoneyEnabled && editChildAllowance 
          ? parseFloat(editChildAllowance) 
          : null
      });
      toast.success(t('settings_saved'));
      setShowEditChildModal(false);
      setEditingChild(null);
      await loadData();
    } catch (error) {
      console.error('Failed to update child settings:', error);
      toast.error(error.response?.data?.detail || (t('failed_to_save')));
    } finally {
      setSavingChildSettings(false);
    }
  };

  // Handle pocket money request response
  const handleRespondToRequest = async (requestId, approved) => {
    setRespondingToRequest(requestId);
    try {
      await respondToPocketMoneyRequest(requestId, approved);
      toast.success(
        approved 
          ? t('request_approved_emoji')
          : t('request_denied')
      );
      await loadData();
    } catch (error) {
      console.error('Failed to respond to request:', error);
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setRespondingToRequest(null);
    }
  };

  // Handle giving pocket money directly
  const handleGivePocketMoney = async () => {
    const amount = parseFloat(pocketMoneyAmount);
    if (isNaN(amount) || amount <= 0 || !selectedChildForPocketMoney) {
      toast.error(t('invalid_amount'));
      return;
    }

    // Determine source based on budget mode
    let effectiveSource = pocketMoneySource;
    if (family?.budget_mode === 'shared') {
      effectiveSource = 'shared'; // Use shared family budget
    } else if (family?.budget_mode === 'separate') {
      effectiveSource = 'personal'; // Use personal balance
    }
    // For hybrid mode, use the selected pocketMoneySource

    try {
      await transferPocketMoney({
        child_member_id: selectedChildForPocketMoney.member_id,
        amount,
        description: t('pocket_money'),
        source: effectiveSource,
        budget_mode: family?.budget_mode
      });
      toast.success(
        t('pocket_money_given').replace('{amount}', amount).replace('{name}', selectedChildForPocketMoney.name)
      );
      setShowGivePocketMoneyModal(false);
      setPocketMoneyAmount('');
      setPocketMoneySource('personal');
      setSelectedChildForPocketMoney(null);
      await loadData();
    } catch (error) {
      console.error('Failed to give pocket money:', error);
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  // Get children with pocket money enabled
  const childrenWithPocketMoney = family?.members?.filter(m => m.role === 'child' && m.has_pocket_money) || [];

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-24 md:pb-8">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')}>
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="font-bold text-lg">{t('family_title')}</h1>
                <p className="text-sm text-muted-foreground">{t('family')}</p>
              </div>
            </div>
            {family && (
              <Button variant="ghost" size="icon" onClick={() => setShowSettingsModal(true)}>
                <Settings className="w-5 h-5" />
              </Button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {!family ? (
          /* No Family - Create or Join */
          <div className="max-w-md mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center mb-8"
            >
              <div className="w-24 h-24 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <Users className="w-12 h-12 text-primary" />
              </div>
              <h2 className="text-2xl font-bold mb-2">{t('no_family')}</h2>
              <p className="text-muted-foreground">{t('no_family_desc')}</p>
            </motion.div>

            <div className="space-y-4">
              <Button 
                onClick={() => setShowCreateModal(true)} 
                className="w-full h-14 text-lg"
                data-testid="create-family-btn"
              >
                <Plus className="w-5 h-5 mr-2" />
                {t('create_family')}
              </Button>
              
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-background px-2 text-muted-foreground">
                    {t('or')}
                  </span>
                </div>
              </div>
              
              <Button 
                variant="outline" 
                onClick={() => setShowJoinModal(true)} 
                className="w-full h-14 text-lg"
                data-testid="join-family-btn"
              >
                <UserPlus className="w-5 h-5 mr-2" />
                {t('join_family')}
              </Button>
            </div>
          </div>
        ) : (
          /* Family Dashboard */
          <div className="space-y-6">
            {/* Family Balance Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <Card className={`${(stats?.available_balance || 0) >= 0 ? 'bg-gradient-to-br from-primary to-orange-600' : 'bg-gradient-to-br from-red-500 to-red-700'}`}>
                <CardContent className="p-6 text-white">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-white/70 text-sm mb-1">{t('family_balance')}</p>
                      <h2 className="text-4xl font-bold" data-testid="family-balance">
                        {formatCurrency(stats?.available_balance || 0)}
                      </h2>
                      <div className="flex gap-4 mt-3 text-sm">
                        <div>
                          <span className="text-white/60">{t('total_income')}:</span>
                          <span className="ml-1 font-medium">{formatCurrency(stats?.total_income || 0)}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 bg-white/10 rounded-full px-3 py-1">
                      {(stats?.available_balance || 0) >= 0 ? (
                        <TrendingUp className="w-4 h-4 text-green-300" />
                      ) : (
                        <TrendingDown className="w-4 h-4 text-red-300" />
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* Invite Code Section - SECURE INVITATIONS */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <UserPlus className="w-5 h-5" />
                    {t('invite_a_member')}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Existing invites */}
                  {family.pending_invites && family.pending_invites.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-muted-foreground">
                        {t('pending_invitations')}
                      </p>
                      {family.pending_invites.map((invite) => (
                        <div key={invite.invite_id || invite.secure_code} className="bg-muted rounded-lg p-3">
                          <div className="flex items-center justify-between mb-2">
                            <div>
                              <p className="font-medium text-sm">{invite.recipient_name || invite.email}</p>
                              <p className="text-xs text-muted-foreground">{invite.email}</p>
                            </div>
                            <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
                              {t('pending')}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <code className="flex-1 text-xs bg-background rounded px-2 py-1 font-mono truncate">
                              {invite.secure_code}
                            </code>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => {
                                navigator.clipboard.writeText(invite.secure_code);
                                toast.success(t('code_copied'));
                              }}
                              title={t('copy_code')}
                            >
                              <Copy className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={async () => {
                                try {
                                  await resendFamilyInvite(invite.invite_id);
                                  toast.success(t('invitation_resent'));
                                  loadData();
                                } catch (error) {
                                  toast.error(error.response?.data?.detail || 'Error');
                                }
                              }}
                              title={t('resend_invitation')}
                            >
                              <RefreshCw className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={async () => {
                                try {
                                  await cancelFamilyInvite(invite.invite_id);
                                  toast.success(t('invitation_cancelled'));
                                  loadData();
                                } catch (error) {
                                  toast.error(error.response?.data?.detail || 'Error');
                                }
                              }}
                              className="text-destructive hover:text-destructive"
                              title={t('cancel_invitation')}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Create new invite button - with subscription limit check */}
                  {family.subscription_info?.can_add_more ? (
                    <Button 
                      variant="outline" 
                      className="w-full"
                      onClick={() => setShowInviteModal(true)}
                      data-testid="invite-member-btn"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      {t('create_invitation')}
                    </Button>
                  ) : (
                    <div className="space-y-2">
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
                        <p className="font-medium text-amber-800">
                          {t('member_limit_reached').replace('{max}', family.subscription_info?.max_members)}
                        </p>
                        <p className="text-amber-700 text-xs mt-1">
                          {t('subscription_limited').replace('{plan}', family.subscription_info?.subscription_name)}
                        </p>
                      </div>
                      {family.subscription_info?.subscription !== 'premium' && (
                        <Button 
                          variant="default" 
                          className="w-full"
                          onClick={() => navigate('/settings')}
                        >
                          <Zap className="w-4 h-4 mr-2" />
                          {t('upgrade_subscription')}
                        </Button>
                      )}
                    </div>
                  )}
                  
                  {/* Member count indicator */}
                  {family.subscription_info && (
                    <p className="text-xs text-muted-foreground text-center">
                      {family.subscription_info.current_members + family.subscription_info.pending_invites}
                      /{family.subscription_info.max_members === -1 ? '∞' : family.subscription_info.max_members} 
                      {t('members_count')}
                    </p>
                  )}
                </CardContent>
              </Card>
            </motion.div>

            {/* Pot Commun Section - Only for hybrid mode (in shared mode, everything is already pooled) */}
            {family.budget_mode === 'hybrid' && potCommun && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15 }}
              >
                <Card className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-950/20 dark:to-emerald-950/20 border-green-200 dark:border-green-800">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg flex items-center gap-2 text-green-700 dark:text-green-400">
                        <Banknote className="w-5 h-5" />
                        {t('common_pot')}
                      </CardTitle>
                      <Button 
                        size="sm" 
                        className="bg-green-600 hover:bg-green-700"
                        onClick={() => setShowContributeModal(true)}
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        {t('contribute')}
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {/* Balance */}
                    <div className="mb-4">
                      <p className="text-sm text-muted-foreground mb-1">
                        {t('available_balance')}
                      </p>
                      <p className="text-3xl font-bold text-green-600" data-testid="pot-commun-balance">
                        {formatCurrency(potCommun.balance || 0)}
                      </p>
                    </div>
                    
                    {/* Monthly target and spending */}
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div className="bg-white/50 dark:bg-white/5 rounded-lg p-3">
                        <p className="text-xs text-muted-foreground">
                          {t('monthly_budget')}
                        </p>
                        <p className="font-bold text-green-600">
                          {formatCurrency(potCommun.monthly_target || potCommun.expected_contributions || 0)}
                        </p>
                      </div>
                      <div className="bg-white/50 dark:bg-white/5 rounded-lg p-3">
                        <p className="text-xs text-muted-foreground">
                          {t('spent_this_month')}
                        </p>
                        <p className="font-bold text-red-500">
                          -{formatCurrency(potCommun.spent_this_month || 0)}
                        </p>
                      </div>
                    </div>
                    
                    {/* Spending progress bar */}
                    {potCommun.monthly_target > 0 && (
                      <div className="mb-4">
                        <div className="flex justify-between text-xs mb-1">
                          <span className="text-muted-foreground">
                            {t('budget_usage')}
                          </span>
                          <span className={potCommun.spent_this_month > potCommun.monthly_target ? 'text-red-500' : 'text-muted-foreground'}>
                            {((potCommun.spent_this_month / potCommun.monthly_target) * 100).toFixed(0)}%
                          </span>
                        </div>
                        <Progress 
                          value={Math.min(100, (potCommun.spent_this_month / potCommun.monthly_target) * 100)} 
                          className="h-2"
                        />
                      </div>
                    )}
                    
                    {/* Spending breakdown by user */}
                    {potCommun.spending_by_user && Object.keys(potCommun.spending_by_user).length > 0 && (
                      <div className="mb-4 bg-white/30 dark:bg-white/5 rounded-lg p-3">
                        <p className="text-xs font-medium text-muted-foreground mb-2 uppercase tracking-wide">
                          {t('common_expenses_by_member')}
                        </p>
                        <div className="space-y-2">
                          {Object.entries(potCommun.spending_by_user).map(([name, data]) => {
                            const percentage = potCommun.spent_this_month > 0
                              ? (data.total / potCommun.spent_this_month) * 100
                              : 0;
                            return (
                              <div key={name}>
                                <div className="flex justify-between text-sm mb-1">
                                  <span className="font-medium">{name}</span>
                                  <span className="text-red-500">
                                    -{formatCurrency(data.total)} ({percentage.toFixed(0)}%)
                                  </span>
                                </div>
                                <Progress value={percentage} className="h-1.5" />
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    
                    {/* Members contributions */}
                    {potCommun.members_contributions && potCommun.members_contributions.length > 0 && (
                      <div>
                        <p className="text-xs font-medium text-muted-foreground mb-2">
                          {t('expected_contributions')}
                        </p>
                        <div className="space-y-2">
                          {potCommun.members_contributions.map((member) => (
                            <div key={member.member_id} className="flex items-center justify-between text-sm">
                              <span>{member.name}</span>
                              <div className="flex items-center gap-2">
                                <span className="text-muted-foreground">
                                  {formatCurrency(member.contribution || 0)}/mois
                                </span>
                                {member.contributed_this_month > 0 && (
                                  <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                                    +{formatCurrency(member.contributed_this_month)}
                                  </span>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            )}

            {/* Members Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <Card>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Users className="w-5 h-5" />
                      {t('household_members')}
                    </CardTitle>
                    <Button variant="ghost" size="sm" onClick={() => setShowAddMemberModal(true)} data-testid="add-member-btn">
                      <Plus className="w-4 h-4 mr-1" /> {t('add_member')}
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {family.members?.map((member) => {
                      const RoleIcon = getRoleIcon(member.role);
                      // Get spending from member.spending_this_month (returned by API)
                      const memberSpending = member.spending_this_month?.total || 0;
                      const isCurrentUser = member.user_id === user?.user_id;
                      const canEditSalary = isCurrentUser && member.role !== 'child';
                      const canDelete = isOwner && !isCurrentUser && member.role !== 'owner';
                      
                      return (
                        <div 
                          key={member.member_id} 
                          className={`flex items-center justify-between p-3 rounded-xl ${isCurrentUser ? 'bg-primary/10 border border-primary/20' : 'bg-muted/50'}`}
                          data-testid={`member-${member.member_id}`}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-full bg-background flex items-center justify-center ${getRoleColor(member.role)}`}>
                              <RoleIcon className="w-5 h-5" />
                            </div>
                            <div>
                              <p className="font-medium flex items-center gap-2">
                                {member.name}
                                {isCurrentUser && (
                                  <span className="text-xs bg-primary/20 text-primary px-2 py-0.5 rounded-full">
                                    {t('you')}
                                  </span>
                                )}
                                {member.role === 'owner' && (
                                  <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
                                    {t('role_owner')}
                                  </span>
                                )}
                              </p>
                              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                {member.role !== 'child' && (
                                  <div className="flex items-center gap-1">
                                    <Banknote className="w-3.5 h-3.5" />
                                    {member.income_type && member.income_type !== 'employed' ? (
                                      <span>{t(`status_${member.income_type}`)}</span>
                                    ) : (
                                      <span>{member.salary ? formatCurrency(member.salary) : t('not_set')}/mois</span>
                                    )}
                                    {canEditSalary && (
                                      <button 
                                        onClick={handleOpenEditSalary}
                                        className="ml-1 p-1 hover:bg-background rounded-md transition-colors"
                                        title={t('edit_salary')}
                                        data-testid="edit-salary-btn"
                                      >
                                        <Pencil className="w-3.5 h-3.5 text-primary" />
                                      </button>
                                    )}
                                  </div>
                                )}
                                {member.role === 'child' && (
                                  <div className="flex items-center gap-2">
                                    <span className="text-pink-500">{t('role_child')}</span>
                                    {member.has_pocket_money && (
                                      <span className="text-xs bg-pink-100 dark:bg-pink-900/30 text-pink-600 dark:text-pink-400 px-2 py-0.5 rounded-full flex items-center gap-1">
                                        <PiggyBank className="w-3 h-3" />
                                        {formatCurrency(member.pocket_money_balance || 0)}
                                      </span>
                                    )}
                                    {isOwner && (
                                      <button 
                                        onClick={() => openEditChildModal(member)}
                                        className="ml-1 p-1 hover:bg-pink-100 dark:hover:bg-pink-900/30 rounded-md transition-colors"
                                        title={t('manage_pocket_money')}
                                        data-testid={`edit-child-${member.member_id}`}
                                      >
                                        <Settings className="w-3.5 h-3.5 text-pink-500" />
                                      </button>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            {/* Hide spending on mobile to prevent layout distortion */}
                            <div className="text-right hidden sm:block">
                              <p className="text-sm text-muted-foreground">
                                {member.role === 'child' && member.has_pocket_money 
                                  ? (t('pocket_money'))
                                  : t('expenses')
                                }
                              </p>
                              <p className={`font-bold ${member.role === 'child' && member.has_pocket_money ? 'text-pink-500' : 'text-destructive'}`}>
                                {member.role === 'child' && member.has_pocket_money 
                                  ? formatCurrency(member.pocket_money_balance || 0)
                                  : `-${formatCurrency(memberSpending)}`
                                }
                              </p>
                            </div>
                            {canDelete && (
                              <button
                                onClick={() => setMemberToDelete(member)}
                                className="p-2 hover:bg-destructive/10 rounded-lg transition-colors group"
                                title={t('remove_from_household')}
                                data-testid={`delete-member-${member.member_id}`}
                              >
                                <Trash2 className="w-4 h-4 text-muted-foreground group-hover:text-destructive" />
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  
                  {/* Invite buttons */}
                  <div className="mt-4 pt-4 border-t border-border flex gap-2">
                    <Button variant="outline" className="flex-1" onClick={() => setShowInviteModal(true)}>
                      <Mail className="w-4 h-4 mr-2" />
                      {t('invite_by_email')}
                    </Button>
                  </div>
                  
                  {/* Transfer ownership button - only for owner with eligible members */}
                  {isOwner && eligibleForTransfer.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-border">
                      <Button 
                        variant="ghost" 
                        className="w-full text-muted-foreground hover:text-foreground"
                        onClick={() => setShowTransferModal(true)}
                        data-testid="transfer-ownership-btn"
                      >
                        <ArrowRightLeft className="w-4 h-4 mr-2" />
                        {t('transfer_ownership')}
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>

            {/* Pocket Money Requests - Only show for adults if there are pending requests */}
            {pocketMoneyRequests.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.25 }}
              >
                <Card className="border-pink-200 dark:border-pink-900 bg-pink-50/50 dark:bg-pink-950/20">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg flex items-center gap-2 text-pink-600 dark:text-pink-400">
                        <Gift className="w-5 h-5" />
                        {t('pocket_money_requests')}
                        <span className="ml-2 bg-pink-500 text-white text-xs px-2 py-0.5 rounded-full">
                          {pocketMoneyRequests.length}
                        </span>
                      </CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {pocketMoneyRequests.map((req) => (
                        <div 
                          key={req.request_id}
                          className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm"
                        >
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-full bg-pink-100 dark:bg-pink-900/30 flex items-center justify-center">
                                <Baby className="w-5 h-5 text-pink-500" />
                              </div>
                              <div>
                                <p className="font-medium">{req.child_name}</p>
                                <p className="text-xs text-muted-foreground flex items-center gap-1">
                                  <Clock className="w-3 h-3" />
                                  {t('pending')}
                                </p>
                              </div>
                            </div>
                            <p className="text-xl font-bold text-pink-500">{formatCurrency(req.amount)}</p>
                          </div>
                          
                          {req.reason && (
                            <p className="text-sm text-muted-foreground mb-3 bg-gray-50 dark:bg-gray-900 rounded-lg p-2">
                              "{req.reason}"
                            </p>
                          )}
                          
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              className="flex-1 text-red-500 hover:text-red-600 hover:bg-red-50"
                              onClick={() => handleRespondToRequest(req.request_id, false)}
                              disabled={respondingToRequest === req.request_id}
                            >
                              <XCircle className="w-4 h-4 mr-1" />
                              {t('deny')}
                            </Button>
                            <Button
                              size="sm"
                              className="flex-1 bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600"
                              onClick={() => handleRespondToRequest(req.request_id, true)}
                              disabled={respondingToRequest === req.request_id}
                            >
                              {respondingToRequest === req.request_id ? (
                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                              ) : (
                                <>
                                  <CheckCircle className="w-4 h-4 mr-1" />
                                  {t('accept')}
                                </>
                              )}
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            )}

            {/* Spending by Member (using spending_by_user which groups by who made the expense) */}
            {stats?.spending_by_user && Object.keys(stats.spending_by_user).length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg">{t('spending_by_member')}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {/* Total Family Spending Summary */}
                      <div className="bg-muted/50 rounded-xl p-4 mb-4">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <Users className="w-5 h-5 text-primary" />
                            <span className="font-semibold">
                              {t('total_family')}
                            </span>
                          </div>
                          <span className="text-xl font-bold text-destructive">
                            -{formatCurrency(stats.total_spending || 0)}
                          </span>
                        </div>
                        <Progress value={100} className="h-2 bg-muted" />
                      </div>

                      {/* Individual Member Breakdown - using spending_by_user */}
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                        {t('breakdown_by_member')}
                      </p>
                      {Object.entries(stats.spending_by_user).map(([name, data]) => {
                        const percentage = stats.total_spending > 0 
                          ? (data.total / stats.total_spending) * 100 
                          : 0;
                        
                        return (
                          <div key={name}>
                            <div className="flex justify-between text-sm mb-1">
                              <span className="font-medium">{name}</span>
                              <span className="text-muted-foreground">
                                {formatCurrency(data.total)} ({percentage.toFixed(0)}%)
                              </span>
                            </div>
                            <Progress value={percentage} className="h-2" />
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            )}

            {/* Subscriptions Summary */}
            {subscriptionStats && (subscriptionStats.spent_this_month > 0 || (subscriptionStats.upcoming && subscriptionStats.upcoming.length > 0)) && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.35 }}
              >
                <Card className="bg-gradient-to-br from-orange-50 to-amber-50 dark:from-orange-950/20 dark:to-amber-950/20 border-orange-200 dark:border-orange-800">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg flex items-center gap-2 text-orange-700 dark:text-orange-400">
                        <RefreshCw className="w-5 h-5" />
                        {t('monthly_subscriptions')}
                      </CardTitle>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => navigate('/subscriptions')}
                        className="text-orange-600 border-orange-300 hover:bg-orange-100"
                      >
                        {t('manage')}
                        <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {/* Total spent this month */}
                    <div className="mb-4">
                      <p className="text-sm text-muted-foreground">
                        {t('total_subscriptions_month')}
                      </p>
                      <p className="text-2xl font-bold text-orange-600">
                        -{formatCurrency(subscriptionStats.spent_this_month || 0)}
                      </p>
                    </div>
                    
                    {/* By category breakdown */}
                    {subscriptionStats.by_category && Object.keys(subscriptionStats.by_category).length > 0 && (
                      <div className="space-y-2 mb-4">
                        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                          {t('by_category')}
                        </p>
                        {Object.entries(subscriptionStats.by_category).map(([cat, data]) => {
                          const percentage = subscriptionStats.spent_this_month > 0
                            ? (data.total / subscriptionStats.spent_this_month) * 100
                            : 0;
                          return (
                            <div key={cat}>
                              <div className="flex justify-between text-sm mb-1">
                                <span className="capitalize">{cat}</span>
                                <span className="text-muted-foreground">
                                  {formatCurrency(data.total)}
                                </span>
                              </div>
                              <Progress value={percentage} className="h-1.5" />
                            </div>
                          );
                        })}
                      </div>
                    )}
                    
                    {/* Upcoming subscriptions */}
                    {subscriptionStats.upcoming && subscriptionStats.upcoming.length > 0 && (
                      <div>
                        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
                          {t('upcoming_debits')}
                        </p>
                        <div className="space-y-2">
                          {subscriptionStats.upcoming.slice(0, 3).map((sub) => (
                            <div key={sub.subscription_id} className="flex items-center justify-between text-sm bg-white/50 dark:bg-white/5 rounded-lg p-2">
                              <span>{sub.name}</span>
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-muted-foreground">
                                  {sub.next_payment ? new Date(sub.next_payment).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' }) : `Le ${sub.payment_day}`}
                                </span>
                                <span className="font-medium text-orange-600">
                                  -{formatCurrency(sub.amount)}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            )}

            {/* Spending by Category */}
            {stats?.spending_by_category && Object.keys(stats.spending_by_category).length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
              >
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg">
                      {t('spending_by_category_title')}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {Object.entries(stats.spending_by_category)
                        .sort((a, b) => b[1].total - a[1].total)
                        .slice(0, 5)
                        .map(([category, data]) => {
                          const percentage = stats.total_spending > 0 
                            ? (data.total / stats.total_spending) * 100 
                            : 0;
                          
                          return (
                            <div key={category}>
                              <div className="flex justify-between text-sm mb-1">
                                <span className="font-medium">{t(`cat_${(category || 'autre').toLowerCase()}`)}</span>
                                <span className="text-muted-foreground">
                                  {formatCurrency(data.total)}
                                </span>
                              </div>
                              <div className="h-2 bg-muted rounded-full overflow-hidden">
                                <div
                                  className="h-full transition-all duration-500"
                                  style={{
                                    width: `${percentage}%`,
                                    backgroundColor: getCategoryColor(category)
                                  }}
                                />
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            )}

            {/* Pocket Money Dashboard for Parents */}
            {pocketMoneyStats && pocketMoneyStats.children_count > 0 && user?.family_role !== 'child' && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="col-span-full"
              >
                <Card className="border-purple-200 bg-gradient-to-br from-purple-50/50 to-pink-50/50">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="p-2 bg-purple-100 rounded-lg">
                          <PiggyBank className="w-5 h-5 text-purple-600" />
                        </div>
                        <div>
                          <CardTitle className="text-lg">
                            {t('pocket_money')}
                          </CardTitle>
                          <p className="text-sm text-muted-foreground">
                            {pocketMoneyStats.children_count > 1 
                              ? t('children_count_plural').replace('{count}', pocketMoneyStats.children_count)
                              : t('children_count').replace('{count}', pocketMoneyStats.children_count)
                            }
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-muted-foreground">
                          {t('total_this_month')}
                        </p>
                        <p className="text-lg font-bold text-purple-600">
                          {formatCurrency(pocketMoneyStats.total_given_this_month)}
                        </p>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {pocketMoneyStats.children_stats.map((child) => (
                        <div key={child.member_id} className="bg-white rounded-lg border p-4">
                          {/* Child Header - Clickable */}
                          <div 
                            className="flex items-center justify-between cursor-pointer"
                            onClick={() => setExpandedChildStats(expandedChildStats === child.member_id ? null : child.member_id)}
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                                <Baby className="w-5 h-5 text-purple-600" />
                              </div>
                              <div>
                                <p className="font-semibold">{child.name}</p>
                                <p className="text-sm text-muted-foreground">
                                  {t('balance_label')} {formatCurrency(child.current_balance)}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <div className="text-right">
                                <p className="text-sm text-muted-foreground">
                                  {t('this_month')}
                                </p>
                                <p className="font-medium text-green-600">+{formatCurrency(child.this_month_received)}</p>
                              </div>
                              {expandedChildStats === child.member_id ? (
                                <ChevronUp className="w-5 h-5 text-muted-foreground" />
                              ) : (
                                <ChevronDown className="w-5 h-5 text-muted-foreground" />
                              )}
                            </div>
                          </div>

                          {/* Expanded Stats */}
                          <AnimatePresence>
                            {expandedChildStats === child.member_id && (
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.2 }}
                                className="overflow-hidden"
                              >
                                <div className="mt-4 pt-4 border-t space-y-4">
                                  {/* Stats Grid */}
                                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                                    <div className="bg-green-50 rounded-lg p-3 text-center">
                                      <TrendingUp className="w-4 h-4 text-green-600 mx-auto mb-1" />
                                      <p className="text-xs text-muted-foreground">
                                        {t('total_received')}
                                      </p>
                                      <p className="font-bold text-green-600">{formatCurrency(child.all_time_received)}</p>
                                    </div>
                                    <div className="bg-red-50 rounded-lg p-3 text-center">
                                      <TrendingDown className="w-4 h-4 text-red-500 mx-auto mb-1" />
                                      <p className="text-xs text-muted-foreground">
                                        {t('total_spent')}
                                      </p>
                                      <p className="font-bold text-red-500">{formatCurrency(child.all_time_spent)}</p>
                                    </div>
                                    <div className="bg-blue-50 rounded-lg p-3 text-center">
                                      <Wallet className="w-4 h-4 text-blue-600 mx-auto mb-1" />
                                      <p className="text-xs text-muted-foreground">
                                        {t('allowance')}
                                      </p>
                                      <p className="font-bold text-blue-600">{formatCurrency(child.monthly_allowance)}/mo</p>
                                    </div>
                                    <div className="bg-purple-50 rounded-lg p-3 text-center">
                                      <Target className="w-4 h-4 text-purple-600 mx-auto mb-1" />
                                      <p className="text-xs text-muted-foreground">
                                        {t('savings_rate')}
                                      </p>
                                      <p className="font-bold text-purple-600">{child.savings_rate}%</p>
                                    </div>
                                  </div>

                                  {/* Balance History Chart */}
                                  {child.balance_history && child.balance_history.length > 0 && (
                                    <div>
                                      <p className="text-sm font-medium mb-2">
                                        {t('payment_history')}
                                      </p>
                                      <div className="h-32 w-full">
                                        <ResponsiveContainer width="100%" height="100%">
                                          <AreaChart data={child.balance_history}>
                                            <defs>
                                              <linearGradient id={`gradient-${child.member_id}`} x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor="#9333ea" stopOpacity={0.3}/>
                                                <stop offset="95%" stopColor="#9333ea" stopOpacity={0}/>
                                              </linearGradient>
                                            </defs>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                            <XAxis 
                                              dataKey="month" 
                                              tick={{ fontSize: 10 }}
                                              tickLine={false}
                                              axisLine={false}
                                            />
                                            <YAxis 
                                              tick={{ fontSize: 10 }}
                                              tickLine={false}
                                              axisLine={false}
                                              tickFormatter={(v) => `${v}€`}
                                            />
                                            <Tooltip 
                                              formatter={(value) => [`${value}€`, t('cumulative')]}
                                              labelFormatter={(label) => label}
                                            />
                                            <Area 
                                              type="monotone" 
                                              dataKey="cumulative" 
                                              stroke="#9333ea" 
                                              strokeWidth={2}
                                              fill={`url(#gradient-${child.member_id})`}
                                            />
                                          </AreaChart>
                                        </ResponsiveContainer>
                                      </div>
                                    </div>
                                  )}

                                  {/* Top Categories */}
                                  {child.top_categories && child.top_categories.length > 0 && (
                                    <div>
                                      <p className="text-sm font-medium mb-2">
                                        {t('spending_categories')}
                                      </p>
                                      <div className="flex flex-wrap gap-2">
                                        {child.top_categories.map((cat, idx) => (
                                          <span 
                                            key={idx}
                                            className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 rounded-full text-xs"
                                          >
                                            <span 
                                              className="w-2 h-2 rounded-full"
                                              style={{ backgroundColor: getCategoryColor(cat.category) }}
                                            />
                                            {t(`cat_${(cat.category || 'autre').toLowerCase()}`)} ({formatCurrency(cat.amount)})
                                          </span>
                                        ))}
                                      </div>
                                    </div>
                                  )}

                                  {!child.has_account && (
                                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm">
                                      <p className="text-yellow-800">
                                        {t('child_no_account_warning')}
                                      </p>
                                    </div>
                                  )}
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            )}
          </div>
        )}
      </main>

      {/* Create Family Modal */}
      <CreateFamilyModal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={() => {
          setShowCreateModal(false);
          loadData();
          checkAuth();
        }}
      />

      {/* Join Family Modal */}
      <JoinFamilyModal
        open={showJoinModal}
        onClose={() => {
          setShowJoinModal(false);
          setInitialInviteCode('');
        }}
        onSuccess={() => {
          setShowJoinModal(false);
          setInitialInviteCode('');
          loadData();
          checkAuth();
        }}
        initialCode={initialInviteCode}
      />

      {/* Add Member Modal */}
      <AddMemberModal
        open={showAddMemberModal}
        onClose={() => setShowAddMemberModal(false)}
        onSuccess={() => {
          setShowAddMemberModal(false);
          loadData();
        }}
        familyId={family?.family_id}
      />

      {/* Invite Modal */}
      <InviteMemberModal
        open={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        familyCode={family?.invite_code}
        onSuccess={() => {
          setShowInviteModal(false);
          loadData();
        }}
      />

      {/* Settings Modal */}
      <FamilySettingsModal
        open={showSettingsModal}
        onClose={() => setShowSettingsModal(false)}
        family={family}
        isOwner={family?.owner_id === user?.user_id}
        onSuccess={() => {
          setShowSettingsModal(false);
          loadData();
        }}
        onDelete={() => {
          setShowSettingsModal(false);
          setShowDeleteConfirm(true);
        }}
        onLeave={async () => {
          try {
            await leaveFamily();
            toast.success(t('you_left_household'));
            setShowSettingsModal(false);
            loadData();
            checkAuth();
          } catch (error) {
            toast.error(error.response?.data?.detail || 'Error');
          }
        }}
      />

      {/* Delete Confirmation */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('delete_family')}</DialogTitle>
            <DialogDescription>
              {t('delete_family_warning')}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>
              {t('cancel')}
            </Button>
            <Button 
              variant="destructive" 
              onClick={async () => {
                try {
                  await deleteFamily();
                  toast.success(t('household_deleted'));
                  setShowDeleteConfirm(false);
                  loadData();
                  checkAuth();
                } catch (error) {
                  toast.error(error.response?.data?.detail || 'Error');
                }
              }}
            >
              {t('delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Salary Modal */}
      <Dialog open={showEditSalaryModal} onOpenChange={setShowEditSalaryModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Banknote className="w-5 h-5 text-primary" />
              {t('edit_my_salary')}
            </DialogTitle>
            <DialogDescription>
              {t('update_income_desc')}
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <div>
              <label className="text-sm font-medium mb-2 block">
                {t('employment_status')}
              </label>
              <select
                value={editingIncomeType}
                onChange={(e) => setEditingIncomeType(e.target.value)}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                data-testid="edit-income-type"
              >
                <option value="employed">{t('status_employed')}</option>
                <option value="freelance">{t('status_freelance')}</option>
                <option value="student">{t('status_student')}</option>
                <option value="unemployed">{t('status_unemployed')}</option>
                <option value="other">{t('status_other')}</option>
              </select>
            </div>

            {editingIncomeType === 'employed' ? (
              <div className="bg-muted/50 rounded-xl p-4">
                <label className="text-sm font-medium mb-2 block text-muted-foreground">
                  {t('net_monthly_income_euro')}
                </label>
                <div className="relative">
                  <Input
                    type="number"
                    placeholder="2500"
                    value={editingSalary}
                    onChange={(e) => setEditingSalary(e.target.value)}
                    className="text-2xl font-bold text-center pr-8"
                    min="0"
                    step="100"
                    data-testid="edit-salary-input"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground font-medium">€</span>
                </div>
              </div>
            ) : (
              <div className="p-4 bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-900 rounded-xl text-sm text-blue-700 dark:text-blue-300">
                {t('variable_income_hint')}
              </div>
            )}
            
            <div className="text-xs text-muted-foreground space-y-1">
              <p className="flex items-center gap-1">
                <Check className="w-3 h-3 text-green-500" />
                {t('raise_bonus_job')}
              </p>
              <p className="flex items-center gap-1">
                <Check className="w-3 h-3 text-green-500" />
                {t('instant_budget_update')}
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditSalaryModal(false)}>
              {t('cancel')}
            </Button>
            <Button 
              onClick={handleSaveSalary} 
              disabled={(editingIncomeType === 'employed' && !editingSalary) || savingSalary}
              data-testid="save-salary-btn"
            >
              {savingSalary ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  {t('save')}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Member Confirmation Modal */}
      <Dialog open={!!memberToDelete} onOpenChange={() => setMemberToDelete(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <Trash2 className="w-5 h-5" />
              {t('remove_a_member')}
            </DialogTitle>
            <DialogDescription>
              {t('remove_member_confirm').replace('{name}', memberToDelete?.name)}
            </DialogDescription>
          </DialogHeader>
          
          {memberToDelete && (
            <div className="py-4">
              <div className="bg-destructive/10 border border-destructive/20 rounded-xl p-4 space-y-2">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full bg-background flex items-center justify-center ${getRoleColor(memberToDelete.role)}`}>
                    {memberToDelete.role === 'child' ? <Baby className="w-5 h-5" /> : <User className="w-5 h-5" />}
                  </div>
                  <div>
                    <p className="font-medium">{memberToDelete.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {memberToDelete.role === 'adult' && memberToDelete.salary 
                        ? `${formatCurrency(memberToDelete.salary)}/mois`
                        : memberToDelete.role === 'child' 
                          ? (t('child'))
                          : (t('adult'))
                      }
                    </p>
                  </div>
                </div>
                {memberToDelete.role === 'adult' && memberToDelete.salary > 0 && (
                  <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded-lg">
                    {t('budget_reduction_warning').replace('{amount}', formatCurrency(memberToDelete.salary))}
                  </p>
                )}
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setMemberToDelete(null)}>
              {t('cancel')}
            </Button>
            <Button 
              variant="destructive"
              onClick={handleDeleteMember}
              disabled={deletingMember}
              data-testid="confirm-delete-member-btn"
            >
              {deletingMember ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  {t('remove')}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Transfer Ownership Modal */}
      <Dialog open={showTransferModal} onOpenChange={setShowTransferModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowRightLeft className="w-5 h-5 text-primary" />
              {t('transfer_ownership')}
            </DialogTitle>
            <DialogDescription>
              {t('transfer_ownership_description')}
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-3">
            {eligibleForTransfer.map((member) => (
              <button
                key={member.member_id}
                onClick={() => handleTransferOwnership(member.member_id)}
                disabled={transferringOwnership}
                className="w-full p-4 bg-muted rounded-xl flex items-center justify-between hover:bg-muted/80 transition-colors disabled:opacity-50"
                data-testid={`transfer-to-${member.member_id}`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-background flex items-center justify-center text-blue-500">
                    <User className="w-5 h-5" />
                  </div>
                  <div className="text-left">
                    <p className="font-medium">{member.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {member.salary ? `${formatCurrency(member.salary)}/mois` : (t('adult'))}
                    </p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-muted-foreground" />
              </button>
            ))}
            
            {eligibleForTransfer.length === 0 && (
              <p className="text-center text-muted-foreground py-4">
                {t('no_eligible_members_transfer')}
              </p>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTransferModal(false)}>
              {t('cancel')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Contribute to Pot Commun Modal */}
      <ContributePotCommunModal
        open={showContributeModal}
        onClose={() => setShowContributeModal(false)}
        currentBalance={potCommun?.balance || 0}
        onSuccess={() => {
          setShowContributeModal(false);
          loadData();
        }}
      />

      {/* Give Pocket Money Modal */}
      <Dialog open={showGivePocketMoneyModal} onOpenChange={setShowGivePocketMoneyModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Gift className="w-5 h-5 text-pink-500" />
              {t('give_pocket_money')}
            </DialogTitle>
            <DialogDescription>
              {selectedChildForPocketMoney && t('give_money_to').replace('{name}', selectedChildForPocketMoney.name)}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {selectedChildForPocketMoney && (
              <div className="flex items-center gap-3 p-3 bg-pink-50 dark:bg-pink-900/20 rounded-xl">
                <div className="w-12 h-12 rounded-full bg-pink-100 dark:bg-pink-900/30 flex items-center justify-center">
                  <Baby className="w-6 h-6 text-pink-500" />
                </div>
                <div>
                  <p className="font-medium">{selectedChildForPocketMoney.name}</p>
                  <p className="text-sm text-muted-foreground">
                    {t('current_balance')} {formatCurrency(selectedChildForPocketMoney.pocket_money_balance || 0)}
                  </p>
                </div>
              </div>
            )}
            
            <div>
              <label className="text-sm font-medium mb-2 block">
                {t('amount_to_give')}
              </label>
              <div className="relative">
                <Input
                  type="number"
                  placeholder="0"
                  value={pocketMoneyAmount}
                  onChange={(e) => setPocketMoneyAmount(e.target.value)}
                  className="text-2xl font-bold text-center pr-8"
                  data-testid="give-pocket-money-amount"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-xl">€</span>
              </div>
            </div>
            
            {/* Quick amount buttons */}
            <div className="grid grid-cols-4 gap-2">
              {[5, 10, 20, 50].map((amount) => (
                <Button
                  key={amount}
                  variant="outline"
                  size="sm"
                  onClick={() => setPocketMoneyAmount(amount.toString())}
                  className={pocketMoneyAmount === amount.toString() ? 'border-pink-500 bg-pink-50' : ''}
                >
                  {amount}€
                </Button>
              ))}
            </div>
            
            {/* Source info/selection based on budget mode */}
            {family?.budget_mode === 'shared' && (
              <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-xl">
                <div className="flex items-center gap-2 text-blue-600">
                  <Users className="w-4 h-4" />
                  <span className="text-sm font-medium">
                    {t('shared_budget')}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {t('money_from_shared_budget')}
                </p>
              </div>
            )}
            
            {family?.budget_mode === 'separate' && (
              <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-xl">
                <div className="flex items-center gap-2 text-purple-600">
                  <Wallet className="w-4 h-4" />
                  <span className="text-sm font-medium">
                    {t('separate_budget')}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {t('money_from_personal_balance')}
                </p>
              </div>
            )}
            
            {/* Source selection - only for hybrid mode */}
            {family?.budget_mode === 'hybrid' && (
              <div>
                <label className="text-sm font-medium mb-2 block">
                  {t('from_which_balance')}
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    variant={pocketMoneySource === 'personal' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setPocketMoneySource('personal')}
                    className={pocketMoneySource === 'personal' ? 'bg-blue-500 hover:bg-blue-600' : ''}
                    data-testid="source-personal"
                  >
                    <Wallet className="w-4 h-4 mr-2" />
                    {t('my_balance')}
                  </Button>
                  <Button
                    variant={pocketMoneySource === 'common' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setPocketMoneySource('common')}
                    className={pocketMoneySource === 'common' ? 'bg-green-500 hover:bg-green-600' : ''}
                    data-testid="source-common"
                  >
                    <PiggyBank className="w-4 h-4 mr-2" />
                    {t('common_pot')}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {pocketMoneySource === 'common' 
                    ? t('common_pot_balance').replace('{amount}', formatCurrency(family?.common_pot_balance || 0))
                    : t('money_deducted_personal')
                  }
                </p>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowGivePocketMoneyModal(false);
              setPocketMoneyAmount('');
              setSelectedChildForPocketMoney(null);
            }}>
              {t('cancel')}
            </Button>
            <Button 
              onClick={handleGivePocketMoney}
              disabled={!pocketMoneyAmount || parseFloat(pocketMoneyAmount) <= 0}
              className="bg-gradient-to-r from-pink-500 to-purple-500"
            >
              <Send className="w-4 h-4 mr-2" />
              {t('give')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Child Pocket Money Modal */}
      <Dialog open={showEditChildModal} onOpenChange={setShowEditChildModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PiggyBank className="w-5 h-5 text-pink-500" />
              {t('pocket_money_settings')}
            </DialogTitle>
            <DialogDescription>
              {editingChild && t('manage_pocket_money_for').replace('{name}', editingChild.name)}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {editingChild && (
              <div className="flex items-center gap-3 p-3 bg-pink-50 dark:bg-pink-900/20 rounded-xl">
                <div className="w-12 h-12 rounded-full bg-pink-100 dark:bg-pink-900/30 flex items-center justify-center">
                  <Baby className="w-6 h-6 text-pink-500" />
                </div>
                <div>
                  <p className="font-medium">{editingChild.name}</p>
                  <p className="text-sm text-muted-foreground">
                    {t('balance_label')} {formatCurrency(editingChild.pocket_money_balance || 0)}
                  </p>
                </div>
              </div>
            )}
            
            {/* Toggle pocket money */}
            <div className="flex items-center justify-between p-3 bg-muted/50 rounded-xl">
              <div>
                <p className="font-medium">
                  {t('enable_pocket_money_child')}
                </p>
                <p className="text-sm text-muted-foreground">
                  {t('allow_child_manage_money')}
                </p>
              </div>
              <button
                onClick={() => setEditChildPocketMoneyEnabled(!editChildPocketMoneyEnabled)}
                className={`w-12 h-6 rounded-full transition-colors relative ${
                  editChildPocketMoneyEnabled ? 'bg-pink-500' : 'bg-gray-300 dark:bg-gray-600'
                }`}
                data-testid="toggle-pocket-money"
              >
                <div className={`w-5 h-5 bg-white rounded-full absolute top-0.5 transition-transform ${
                  editChildPocketMoneyEnabled ? 'translate-x-6' : 'translate-x-0.5'
                }`} />
              </button>
            </div>
            
            {/* Monthly allowance - only show when enabled */}
            {editChildPocketMoneyEnabled && (
              <div>
                <label className="text-sm font-medium mb-2 block">
                  {t('monthly_allowance_optional')}
                </label>
                <div className="relative">
                  <Input
                    type="number"
                    placeholder="0"
                    value={editChildAllowance}
                    onChange={(e) => setEditChildAllowance(e.target.value)}
                    className="pr-8"
                    data-testid="child-allowance-input"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">€/mois</span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {t('monthly_auto_amount_desc')}
                </p>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowEditChildModal(false);
              setEditingChild(null);
            }}>
              {t('cancel')}
            </Button>
            <Button 
              onClick={handleSaveChildSettings}
              disabled={savingChildSettings}
              className="bg-gradient-to-r from-pink-500 to-purple-500"
            >
              {savingChildSettings ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Check className="w-4 h-4 mr-2" />
              )}
              {t('save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Onboarding Tutorial */}
      <Suspense fallback={null}>
        {tutorialActive && (
          <OnboardingTutorial onComplete={() => setTutorialActive(false)} />
        )}
      </Suspense>

      {/* Mobile Bottom Navigation */}
      {/* Spending by member (shared/hybrid mode only) */}
      <div className="mt-6">
        <SpendingByMember />
      </div>
      {/* Bottom Navigation */}
      <BottomNav />
    </div>
  );
}

// Create Family Modal Component
function CreateFamilyModal({ open, onClose, onSuccess }) {
  const { t, language } = useLanguage();
  const [name, setName] = useState('');
  const [budgetMode, setBudgetMode] = useState('shared');
  const [commonPercentage, setCommonPercentage] = useState(50);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!name.trim()) return;
    
    setIsSubmitting(true);
    try {
      const data = { 
        name: name.trim(), 
        budget_mode: budgetMode
      };
      await createFamily(data);
      toast.success(t('family_created'));
      setName('');
      setBudgetMode('shared');
      setCommonPercentage(50);
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error creating family');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('create_family')}</DialogTitle>
          <DialogDescription>
            {t('create_family_description')}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div>
            <label className="text-sm font-medium mb-2 block">{t('family_name')}</label>
            <Input
              placeholder={t('example_family_name')}
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="family-name-input"
            />
          </div>
          
          <div>
            <label className="text-sm font-medium mb-2 block">{t('budget_mode')}</label>
            <div className="grid grid-cols-3 gap-2">
              <button
                onClick={() => setBudgetMode('shared')}
                className={`p-3 rounded-xl border-2 transition-all text-left ${
                  budgetMode === 'shared'
                    ? 'border-primary bg-primary/10'
                    : 'border-border hover:border-primary/50'
                }`}
              >
                <PiggyBank className="w-5 h-5 mb-2 text-primary" />
                <p className="font-medium text-xs">{t('budget_shared')}</p>
                <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{t('budget_mode_shared_desc')}</p>
              </button>
              
              <button
                onClick={() => setBudgetMode('separate')}
                className={`p-3 rounded-xl border-2 transition-all text-left ${
                  budgetMode === 'separate'
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-border hover:border-blue-300'
                }`}
              >
                <Users className="w-5 h-5 mb-2 text-blue-500" />
                <p className="font-medium text-xs">{t('budget_separate')}</p>
                <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{t('budget_mode_separate_desc')}</p>
              </button>
              
              <button
                onClick={() => setBudgetMode('hybrid')}
                className={`p-3 rounded-xl border-2 transition-all text-left ${
                  budgetMode === 'hybrid'
                    ? 'border-green-500 bg-green-50'
                    : 'border-border hover:border-green-300'
                }`}
              >
                <div className="flex gap-1 mb-2">
                  <PiggyBank className="w-4 h-4 text-green-500" />
                  <Users className="w-4 h-4 text-green-500" />
                </div>
                <p className="font-medium text-xs">{t('budget_hybrid')}</p>
                <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{t('budget_mode_hybrid_desc')}</p>
              </button>
            </div>
          </div>
          
          {/* Common budget percentage for hybrid mode */}
          {budgetMode === 'hybrid' && (
            <div className="bg-green-50 rounded-xl p-4">
              <label className="text-sm font-medium mb-3 block">
                {t('common_percentage')}
              </label>
              <div className="flex items-center gap-4">
                <input
                  type="range"
                  min="10"
                  max="90"
                  step="5"
                  value={commonPercentage}
                  onChange={(e) => setCommonPercentage(parseInt(e.target.value))}
                  className="flex-1 accent-green-500"
                />
                <span className="text-2xl font-bold text-green-600 w-16 text-right">{commonPercentage}%</span>
              </div>
              <div className="flex justify-between text-xs text-muted-foreground mt-2">
                <span>{t('personal')}: {100 - commonPercentage}%</span>
                <span>{t('common')}: {commonPercentage}%</span>
              </div>
            </div>
          )}
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t('cancel')}</Button>
          <Button onClick={handleSubmit} disabled={!name.trim() || isSubmitting} data-testid="create-family-submit">
            {isSubmitting ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              t('create_family')
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Join Family Modal Component
function JoinFamilyModal({ open, onClose, onSuccess, initialCode = '' }) {
  const { t, language } = useLanguage();
  const [code, setCode] = useState(initialCode);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Update code when initialCode changes (e.g., from invite link)
  useEffect(() => {
    if (initialCode) {
      setCode(initialCode);
    }
  }, [initialCode]);

  const handleSubmit = async () => {
    if (!code.trim()) return;
    
    setIsSubmitting(true);
    try {
      const res = await joinFamily(code.trim().toUpperCase());
      toast.success(res.data.message);
      setCode('');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid code');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('join_family')}</DialogTitle>
          <DialogDescription>
            {t('enter_invite_code_desc')}
          </DialogDescription>
        </DialogHeader>
        
        <div className="py-4 space-y-2">
          <Input
            placeholder="INV-XXXXXX-XXXXXXXX"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            className="text-center text-xl font-mono tracking-wider"
            maxLength={20}
            data-testid="join-family-code-input"
          />
          <p className="text-xs text-muted-foreground text-center">
            {t('invite_code_validity')}
          </p>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t('cancel')}</Button>
          <Button onClick={handleSubmit} disabled={!code.trim() || code.length < 15 || isSubmitting} data-testid="join-family-submit">
            {isSubmitting ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              t('join_family')
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Add Member Modal Component
function AddMemberModal({ open, onClose, onSuccess, familyId }) {
  const { t, language } = useLanguage();
  const [step, setStep] = useState(1); // 1: info base, 2: compte (pour enfants avec argent de poche)
  const [name, setName] = useState('');
  const [role, setRole] = useState('child');
  const [salary, setSalary] = useState('');
  const [incomeType, setIncomeType] = useState('employed'); // employed, freelance, student, unemployed, other
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Pocket money fields for children
  const [hasPocketMoney, setHasPocketMoney] = useState(false);
  const [pocketMoneyAllowance, setPocketMoneyAllowance] = useState('');
  const [allowanceFrequency, setAllowanceFrequency] = useState('monthly');
  // Account creation fields
  const [createAccount, setCreateAccount] = useState(false);
  const [authMethod, setAuthMethod] = useState('none'); // none, google, password
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [usernameAvailable, setUsernameAvailable] = useState(null);
  const [usernameError, setUsernameError] = useState('');
  const [passwordStrength, setPasswordStrength] = useState({ score: 0, strength: '' });
  const [checkingUsername, setCheckingUsername] = useState(false);
  const [createdMemberId, setCreatedMemberId] = useState(null);

  // Reset form when modal closes
  useEffect(() => {
    if (!open) {
      setStep(1);
      setName('');
      setRole('child');
      setSalary('');
      setIncomeType('employed');
      setHasPocketMoney(false);
      setPocketMoneyAllowance('');
      setAllowanceFrequency('monthly');
      setCreateAccount(false);
      setAuthMethod('none');
      setEmail('');
      setUsername('');
      setPassword('');
      setConfirmPassword('');
      setUsernameAvailable(null);
      setUsernameError('');
      setPasswordStrength({ score: 0, strength: '' });
      setCreatedMemberId(null);
    }
  }, [open]);

  // Check username availability with debounce
  useEffect(() => {
    if (!username || username.length < 3) {
      setUsernameAvailable(null);
      setUsernameError('');
      return;
    }

    const timer = setTimeout(async () => {
      setCheckingUsername(true);
      try {
        const res = await checkUsername(username);
        setUsernameAvailable(res.data.available);
        setUsernameError(res.data.error || '');
      } catch (e) {
        setUsernameError('Erreur de vérification');
      } finally {
        setCheckingUsername(false);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [username]);

  // Check password strength
  useEffect(() => {
    if (!password) {
      setPasswordStrength({ score: 0, strength: '' });
      return;
    }

    const timer = setTimeout(async () => {
      try {
        const res = await validatePassword(password);
        setPasswordStrength({ score: res.data.score, strength: res.data.strength });
      } catch (e) {
        // Ignore
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [password]);

  const handleStep1Submit = async () => {
    if (!name.trim()) return;
    
    // For adults, require email for invitation
    if (role === 'adult' && !email.trim()) {
      toast.error(t('email_required_adults'));
      return;
    }
    
    setIsSubmitting(true);
    try {
      const result = await addFamilyMember({
        name: name.trim(),
        email: role === 'adult' ? email.trim().toLowerCase() : null,
        role,
        income_type: role === 'adult' ? incomeType : null,
        salary: role === 'adult' && incomeType === 'employed' && salary ? parseFloat(salary) : null,
        has_pocket_money: role === 'child' ? hasPocketMoney : false,
        pocket_money_allowance: role === 'child' && hasPocketMoney && pocketMoneyAllowance 
          ? parseFloat(pocketMoneyAllowance) 
          : null,
        allowance_frequency: role === 'child' && hasPocketMoney ? allowanceFrequency : null
      });
      
      // Check if backend created an invitation (for adults with email)
      if (result.data.requires_invitation) {
        toast.success(t('invitation_sent_to_email').replace('{email}', email));
        onSuccess();
        return;
      }
      
      // If child with pocket money, offer to create account
      if (role === 'child' && hasPocketMoney) {
        setCreatedMemberId(result.data.member_id);
        setStep(2);
        toast.success(t('child_added_create_account'));
      } else {
        toast.success(t('member_added'));
        onSuccess();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCreateAccount = async () => {
    if (authMethod === 'google' && email) {
      // Send invitation with email
      try {
        setIsSubmitting(true);
        await inviteToFamily({
          name: name.trim(),
          email: email.trim(),
          role: 'child',
          has_pocket_money: hasPocketMoney,
          pocket_money_allowance: hasPocketMoney && pocketMoneyAllowance ? parseFloat(pocketMoneyAllowance) : null,
          allowance_frequency: hasPocketMoney ? allowanceFrequency : null
        });
        toast.success(t('email_sent_to') + ' ' + email);
        onSuccess();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Error');
      } finally {
        setIsSubmitting(false);
      }
    } else if (authMethod === 'password' && username && password) {
      // Create account with username/password
      if (password !== confirmPassword) {
        toast.error(t('passwords_dont_match'));
        return;
      }
      if (!usernameAvailable) {
        toast.error(usernameError || t('username_unavailable'));
        return;
      }
      
      try {
        setIsSubmitting(true);
        await createChildAccount({
          username: username.toLowerCase().trim(),
          password,
          name: name.trim(),
          family_id: familyId,
          member_id: createdMemberId
        });
        toast.success(
          t('account_created_login').replace('{name}', name).replace('{username}', username)
        );
        onSuccess();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Error');
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleSkipAccount = () => {
    toast.success(t('member_added'));
    onSuccess();
  };

  const getPasswordStrengthColor = () => {
    switch (passwordStrength.score) {
      case 0: return 'bg-red-500';
      case 1: return 'bg-orange-500';
      case 2: return 'bg-yellow-500';
      case 3: return 'bg-green-500';
      case 4: return 'bg-emerald-500';
      default: return 'bg-gray-300';
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {step === 1 
              ? t('add_member')
              : (t('create_account'))
            }
          </DialogTitle>
          <DialogDescription>
            {step === 1 
              ? t('add_child_or_adult')
              : t('how_will_login').replace('{name}', name)
            }
          </DialogDescription>
        </DialogHeader>
        
        {step === 1 ? (
          // STEP 1: Basic info
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium mb-2 block">{t('member_name')}</label>
              <Input
                placeholder={t('first_name')}
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="member-name-input"
              />
            </div>
            
            <div>
              <label className="text-sm font-medium mb-2 block">{t('member_role')}</label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => setRole('child')}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    role === 'child'
                      ? 'border-pink-500 bg-pink-50 dark:bg-pink-950/30'
                      : 'border-border hover:border-pink-300'
                  }`}
                >
                  <Baby className={`w-6 h-6 mx-auto mb-2 ${role === 'child' ? 'text-pink-500' : 'text-muted-foreground'}`} />
                  <p className="font-medium text-sm">{t('role_child')}</p>
                </button>
                
                <button
                  onClick={() => setRole('adult')}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    role === 'adult'
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-950/30'
                      : 'border-border hover:border-blue-300'
                  }`}
                >
                  <User className={`w-6 h-6 mx-auto mb-2 ${role === 'adult' ? 'text-blue-500' : 'text-muted-foreground'}`} />
                  <p className="font-medium text-sm">{t('role_adult')}</p>
                </button>
              </div>
            </div>
            
            {role === 'adult' && (
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">
                    Email *
                  </label>
                  <Input
                    type="email"
                    placeholder="email@exemple.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="adult-email"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    {t('invitation_will_be_sent')}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">
                    {t('employment_status')}
                  </label>
                  <select
                    value={incomeType}
                    onChange={(e) => setIncomeType(e.target.value)}
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    data-testid="add-member-income-type"
                  >
                    <option value="employed">{t('status_employed')}</option>
                    <option value="freelance">{t('status_freelance')}</option>
                    <option value="student">{t('status_student')}</option>
                    <option value="unemployed">{t('status_unemployed')}</option>
                    <option value="other">{t('status_other')}</option>
                  </select>
                </div>
                {incomeType === 'employed' ? (
                  <div>
                    <label className="text-sm font-medium mb-2 block">
                      {t('member_salary')} <span className="text-muted-foreground">({t('optional')})</span>
                    </label>
                    <div className="relative">
                      <Input
                        type="number"
                        placeholder="0"
                        value={salary}
                        onChange={(e) => setSalary(e.target.value)}
                        className="pr-8"
                        data-testid="add-member-salary"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">€</span>
                    </div>
                  </div>
                ) : (
                  <div className="p-3 bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-900 rounded-lg text-xs text-blue-700 dark:text-blue-300">
                    {t('variable_income_hint')}
                  </div>
                )}
              </div>
            )}
            
            {/* Pocket money section for children */}
            {role === 'child' && (
              <div className="space-y-4 p-4 bg-pink-50 dark:bg-pink-950/20 rounded-xl border border-pink-200 dark:border-pink-900">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <PiggyBank className="w-5 h-5 text-pink-500" />
                    <label className="text-sm font-medium">
                      {t('pocket_money')}
                    </label>
                  </div>
                  <button
                    type="button"
                    onClick={() => setHasPocketMoney(!hasPocketMoney)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      hasPocketMoney ? 'bg-pink-500' : 'bg-gray-300 dark:bg-gray-600'
                    }`}
                    data-testid="pocket-money-toggle"
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        hasPocketMoney ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
                
                <p className="text-xs text-muted-foreground">
                  {t('pocket_money_description')}
                </p>
                
                {hasPocketMoney && (
                  <div className="space-y-3 pt-2 border-t border-pink-200 dark:border-pink-800">
                    <div>
                      <label className="text-sm font-medium mb-1 block">
                        {t('regular_allowance')} 
                        <span className="text-muted-foreground ml-1">({t('optional')})</span>
                      </label>
                      <div className="relative">
                        <Input
                          type="number"
                          placeholder="0"
                          value={pocketMoneyAllowance}
                          onChange={(e) => setPocketMoneyAllowance(e.target.value)}
                          className="pr-8"
                          data-testid="pocket-money-amount"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">€</span>
                      </div>
                    </div>
                    
                    <div>
                      <label className="text-sm font-medium mb-1 block">
                        {t('frequency')}
                      </label>
                      <div className="grid grid-cols-3 gap-2">
                        {[
                          { value: 'weekly', labelFr: 'Hebdo', labelEn: 'Weekly' },
                          { value: 'monthly', labelFr: 'Mensuel', labelEn: 'Monthly' },
                          { value: 'manual', labelFr: 'Manuel', labelEn: 'Manual' }
                        ].map((freq) => (
                          <button
                            key={freq.value}
                            type="button"
                            onClick={() => setAllowanceFrequency(freq.value)}
                            className={`py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                              allowanceFrequency === freq.value
                                ? 'bg-pink-500 text-white'
                                : 'bg-white dark:bg-gray-800 border border-border hover:border-pink-300'
                            }`}
                            data-testid={`frequency-${freq.value}`}
                          >
                            {language === 'fr' ? freq.labelFr : language === 'pt' ? (freq.labelPt || freq.labelEn) : freq.labelEn}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          // STEP 2: Account creation for children - 2 options
          <div className="space-y-4 py-4">
            <p className="text-sm text-muted-foreground text-center">
              {t('how_to_create_account')}
            </p>
            
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => setAuthMethod('password')}
                className={`p-4 rounded-xl border-2 transition-all text-left ${
                  authMethod === 'password'
                    ? 'border-purple-500 bg-purple-50 dark:bg-purple-950/30'
                    : 'border-border hover:border-purple-300'
                }`}
              >
                <User className={`w-6 h-6 mb-2 ${authMethod === 'password' ? 'text-purple-500' : 'text-muted-foreground'}`} />
                <p className="font-medium text-sm">
                  {t('create_credentials')}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {t('you_set_password')}
                </p>
              </button>
              
              <button
                onClick={() => setAuthMethod('google')}
                className={`p-4 rounded-xl border-2 transition-all text-left ${
                  authMethod === 'google'
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-950/30'
                    : 'border-border hover:border-blue-300'
                }`}
              >
                <Mail className={`w-6 h-6 mb-2 ${authMethod === 'google' ? 'text-blue-500' : 'text-muted-foreground'}`} />
                <p className="font-medium text-sm">
                  {t('invite_by_email')}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {t('child_creates_account')}
                </p>
              </button>
            </div>
            
            {authMethod === 'google' && (
              <div className="space-y-3 p-4 bg-blue-50 dark:bg-blue-950/20 rounded-xl">
                <div>
                  <label className="text-sm font-medium mb-2 block">
                    {t('childs_email')}
                  </label>
                  <Input
                    type="email"
                    placeholder="enfant@exemple.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="child-email-input"
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  {t('invitation_will_be_sent_child')}
                </p>
              </div>
            )}
            
            {authMethod === 'password' && (
              <div className="space-y-4 p-4 bg-purple-50 dark:bg-purple-950/20 rounded-xl">
                {/* Username */}
                <div>
                  <label className="text-sm font-medium mb-2 block">
                    {t('username')}
                  </label>
                  <div className="relative">
                    <Input
                      placeholder={t('username_placeholder_' + language)}
                      value={username}
                      onChange={(e) => setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
                      className={`pr-10 ${
                        usernameAvailable === true ? 'border-green-500' :
                        usernameAvailable === false ? 'border-red-500' : ''
                      }`}
                      data-testid="child-username-input"
                    />
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      {checkingUsername ? (
                        <div className="w-4 h-4 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
                      ) : usernameAvailable === true ? (
                        <Check className="w-4 h-4 text-green-500" />
                      ) : usernameAvailable === false ? (
                        <X className="w-4 h-4 text-red-500" />
                      ) : null}
                    </div>
                  </div>
                  {usernameError && (
                    <p className="text-xs text-red-500 mt-1">{usernameError}</p>
                  )}
                  {usernameAvailable && (
                    <p className="text-xs text-green-500 mt-1">
                      {t('available_exclaim')}
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground mt-1">
                    {t('username_requirements')}
                  </p>
                </div>
                
                {/* Password */}
                <div>
                  <label className="text-sm font-medium mb-2 block">
                    {t('password')}
                  </label>
                  <Input
                    type="password"
                    placeholder="••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    data-testid="child-password-input"
                  />
                  {password && (
                    <div className="mt-2">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div 
                            className={`h-full transition-all ${getPasswordStrengthColor()}`}
                            style={{ width: `${(passwordStrength.score / 4) * 100}%` }}
                          />
                        </div>
                        <span className="text-xs text-muted-foreground">
                          {passwordStrength.strength}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Confirm Password */}
                <div>
                  <label className="text-sm font-medium mb-2 block">
                    {t('confirm_password')}
                  </label>
                  <Input
                    type="password"
                    placeholder="••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className={confirmPassword && password !== confirmPassword ? 'border-red-500' : ''}
                    data-testid="child-confirm-password-input"
                  />
                  {confirmPassword && password !== confirmPassword && (
                    <p className="text-xs text-red-500 mt-1">
                      {t('passwords_dont_match')}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
        
        <DialogFooter className="gap-2">
          {step === 1 ? (
            <>
              <Button variant="outline" onClick={onClose}>{t('cancel')}</Button>
              <Button 
                onClick={handleStep1Submit} 
                disabled={!name.trim() || isSubmitting} 
                data-testid="add-member-submit"
              >
                {isSubmitting ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  t('continue_btn')
                )}
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={handleSkipAccount}>
                {t('later')}
              </Button>
              <Button 
                onClick={handleCreateAccount}
                disabled={
                  isSubmitting || 
                  authMethod === 'none' ||
                  (authMethod === 'google' && !email) ||
                  (authMethod === 'password' && (!username || !password || !confirmPassword || password !== confirmPassword || !usernameAvailable))
                }
                data-testid="create-account-submit"
              >
                {isSubmitting ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  t('create_account_btn')
                )}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Invite Member Modal
function InviteMemberModal({ open, onClose, familyCode, onSuccess }) {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [inviteResult, setInviteResult] = useState(null);
  const [limitError, setLimitError] = useState(null);

  const handleSubmit = async () => {
    if (!email.trim() || !name.trim()) return;
    
    setIsSubmitting(true);
    setLimitError(null);
    try {
      const response = await inviteToFamily({ 
        name: name.trim(),
        email: email.trim(), 
        role: 'adult' 
      });
      
      setInviteResult({
        code: response.data.secure_code,
        email: email,
        name: name,
        emailSent: response.data.email_sent
      });
      
      toast.success(response.data.email_sent 
        ? t('invitation_sent_by_email')
        : t('code_created_share_manually')
      );
      
      onSuccess();
    } catch (error) {
      const errorData = error.response?.data?.detail;
      
      // Check if it's a member limit error
      if (errorData?.code === 'MEMBER_LIMIT_REACHED') {
        setLimitError(errorData);
      } else {
        toast.error(typeof errorData === 'string' ? errorData : 'Error');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setName('');
    setEmail('');
    setInviteResult(null);
    setLimitError(null);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {limitError 
              ? (t('limit_reached'))
              : inviteResult 
                ? (t('invitation_created'))
                : t('invite_member')
            }
          </DialogTitle>
        </DialogHeader>
        
        {limitError ? (
          <div className="space-y-4 py-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="font-medium text-amber-800">{limitError.message}</p>
            </div>
            
            {limitError.can_upgrade ? (
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  {t('upgrade_for_more_members')
                    .replace('{plan}', limitError.upgrade_to === 'premium' ? 'Premium' : 'Standard')
                    .replace('{limit}', limitError.upgrade_limit === -1 ? t('unlimited_members') : limitError.upgrade_limit)}
                </p>
                <Button 
                  className="w-full" 
                  onClick={() => {
                    handleClose();
                    navigate('/settings');
                  }}
                >
                  <Zap className="w-4 h-4 mr-2" />
                  {t('view_subscriptions')}
                </Button>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center">
                {t('max_limit_reached')}
              </p>
            )}
            
            <DialogFooter>
              <Button variant="outline" onClick={handleClose}>
                {t('close')}
              </Button>
            </DialogFooter>
          </div>
        ) : !inviteResult ? (
          <>
            <div className="space-y-4 py-4">
              <div>
                <label className="text-sm font-medium mb-2 block">
                  {t('first_name')}
                </label>
                <Input
                  placeholder={t('example_name_marie')}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  data-testid="invite-name-input"
                />
              </div>
              
              <div>
                <label className="text-sm font-medium mb-2 block">Email</label>
                <Input
                  type="email"
                  placeholder="email@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="invite-email-input"
                />
              </div>
              
              <p className="text-xs text-muted-foreground">
                {t('invite_email_auto_sent')}
              </p>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={handleClose}>{t('cancel')}</Button>
              <Button 
                onClick={handleSubmit} 
                disabled={!email.trim() || !name.trim() || isSubmitting}
                data-testid="send-invite-btn"
              >
                {isSubmitting ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  t('send_invitation')
                )}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <div className="py-4 space-y-4">
              <div className="bg-green-50 dark:bg-green-900/20 rounded-xl p-4 text-center">
                <p className="text-sm text-muted-foreground mb-2">
                  {t('code_for')} <strong>{inviteResult.name}</strong>
                </p>
                <p className="text-2xl font-mono font-bold tracking-wider text-green-600">
                  {inviteResult.code}
                </p>
                {inviteResult.emailSent && (
                  <p className="text-xs text-green-600 mt-2">
                    ✉️ {t('email_sent_to')} {inviteResult.email}
                  </p>
                )}
              </div>
              
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => {
                    navigator.clipboard.writeText(inviteResult.code);
                    toast.success(t('code_copied'));
                  }}
                >
                  <Copy className="w-4 h-4 mr-2" />
                  {t('copy_code')}
                </Button>
              </div>
              
              <p className="text-xs text-muted-foreground text-center">
                {t('code_single_use_warning')}
              </p>
            </div>
            
            <DialogFooter>
              <Button onClick={handleClose}>
                {t('close')}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

// Family Settings Modal
function FamilySettingsModal({ open, onClose, family, isOwner, onSuccess, onDelete, onLeave }) {
  const { t, language } = useLanguage();
  const [name, setName] = useState(family?.name || '');
  const [budgetMode, setBudgetMode] = useState(family?.budget_mode || 'shared');
  const [commonPercentage, setCommonPercentage] = useState(family?.common_budget_percentage || 50);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (family) {
      setName(family.name);
      setBudgetMode(family.budget_mode);
      setCommonPercentage(family.common_budget_percentage || 50);
    }
  }, [family]);

  const handleSave = async () => {
    setIsSubmitting(true);
    try {
      await updateFamilySettings({ 
        name, 
        budget_mode: budgetMode,
        common_budget_percentage: budgetMode === 'hybrid' ? commonPercentage : null
      });
      toast.success(t('settings_updated'));
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('settings')}</DialogTitle>
        </DialogHeader>
        
        {isOwner ? (
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium mb-2 block">{t('family_name')}</label>
              <Input value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            
            <div>
              <label className="text-sm font-medium mb-2 block">{t('budget_mode')}</label>
              <div className="grid grid-cols-3 gap-2">
                <button
                  onClick={() => setBudgetMode('shared')}
                  className={`p-3 rounded-xl border-2 transition-all ${
                    budgetMode === 'shared'
                      ? 'border-primary bg-primary/10'
                      : 'border-border'
                  }`}
                >
                  <PiggyBank className="w-5 h-5 mx-auto mb-1 text-primary" />
                  <p className="font-medium text-xs">{t('budget_shared')}</p>
                </button>
                <button
                  onClick={() => setBudgetMode('separate')}
                  className={`p-3 rounded-xl border-2 transition-all ${
                    budgetMode === 'separate'
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-border'
                  }`}
                >
                  <Users className="w-5 h-5 mx-auto mb-1 text-blue-500" />
                  <p className="font-medium text-xs">{t('budget_separate')}</p>
                </button>
                <button
                  onClick={() => setBudgetMode('hybrid')}
                  className={`p-3 rounded-xl border-2 transition-all ${
                    budgetMode === 'hybrid'
                      ? 'border-green-500 bg-green-50'
                      : 'border-border'
                  }`}
                >
                  <div className="flex justify-center gap-1 mb-1">
                    <PiggyBank className="w-4 h-4 text-green-500" />
                    <Users className="w-4 h-4 text-green-500" />
                  </div>
                  <p className="font-medium text-xs">{t('budget_hybrid')}</p>
                </button>
              </div>
            </div>
            
            {/* Common budget percentage for hybrid mode */}
            {budgetMode === 'hybrid' && (
              <div className="bg-green-50 rounded-xl p-4">
                <label className="text-sm font-medium mb-3 block">
                  {t('common_percentage')}
                </label>
                <div className="flex items-center gap-4">
                  <input
                    type="range"
                    min="10"
                    max="90"
                    step="5"
                    value={commonPercentage}
                    onChange={(e) => setCommonPercentage(parseInt(e.target.value))}
                    className="flex-1 accent-green-500"
                  />
                  <span className="text-2xl font-bold text-green-600 w-16 text-right">{commonPercentage}%</span>
                </div>
                <div className="flex justify-between text-xs text-muted-foreground mt-2">
                  <span>{t('personal')}: {100 - commonPercentage}%</span>
                  <span>{t('common')}: {commonPercentage}%</span>
                </div>
              </div>
            )}
            
            <div className="pt-4 border-t">
              <Button variant="destructive" className="w-full" onClick={onDelete}>
                <Trash2 className="w-4 h-4 mr-2" />
                {t('delete_family')}
              </Button>
            </div>
          </div>
        ) : (
          <div className="py-4">
            <p className="text-muted-foreground mb-4">
              {t('only_owner_can_modify')}
            </p>
            <Button variant="destructive" className="w-full" onClick={onLeave}>
              <LogOut className="w-4 h-4 mr-2" />
              {t('leave_family')}
            </Button>
          </div>
        )}
        
        {isOwner && (
          <DialogFooter>
            <Button variant="outline" onClick={onClose}>{t('cancel')}</Button>
            <Button onClick={handleSave} disabled={isSubmitting}>
              {isSubmitting ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                t('save')
              )}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}

// Contribute to Pot Commun Modal
function ContributePotCommunModal({ open, onClose, currentBalance, onSuccess }) {
  const { language, t } = useLanguage();
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    const numAmount = parseFloat(amount);
    if (!numAmount || numAmount <= 0) {
      toast.error(t('invalid_amount'));
      return;
    }
    
    setIsSubmitting(true);
    try {
      await contributeToPotCommun({
        amount: numAmount,
        description: description || t('common_pot_contribution')
      });
      toast.success(t('contribution_added'));
      setAmount('');
      setDescription('');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const presetAmounts = [50, 100, 200, 500];

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Banknote className="w-5 h-5 text-green-500" />
            {t('contribute_common_pot')}
          </DialogTitle>
          <DialogDescription>
            {t('current_balance')}: {formatCurrency(currentBalance)}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {/* Amount input */}
          <div>
            <label className="text-sm font-medium mb-2 block">
              {t('amount')}
            </label>
            <div className="relative">
              <Input
                type="number"
                placeholder="0"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="text-2xl font-bold pr-12 h-14"
              />
              <span className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground font-medium">
                €
              </span>
            </div>
          </div>
          
          {/* Preset amounts */}
          <div className="flex gap-2">
            {presetAmounts.map((preset) => (
              <Button
                key={preset}
                variant="outline"
                size="sm"
                className="flex-1"
                onClick={() => setAmount(preset.toString())}
              >
                {preset}€
              </Button>
            ))}
          </div>
          
          {/* Description */}
          <div>
            <label className="text-sm font-medium mb-2 block">
              {t('description_optional')}
            </label>
            <Input
              placeholder={t('example_monthly_groceries')}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t('cancel')}
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={!amount || isSubmitting}
            className="bg-green-600 hover:bg-green-700"
          >
            {isSubmitting ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <>
                <Plus className="w-4 h-4 mr-1" />
                {t('add')}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
